<template>
    <div
        class="slider-container"
        :class="short? 'col col-1': ''"
    >
        <label :for="'input-' + id">{{ labelText }}
            <tooltip :text="tooltipText"/>
        </label>
        <div
            class="row"
            :class="short? 'layout-responsive-22': 'layout-responsive-10'"
        >
            <div
                class="col col-1 hid-phone hid-print"
                :class="fakeslider ? 'col-fakeslider' : 'col-slider'"
            >
                <div
                    :id="id"
                    ref="slider"
                    :class="fakeslider? 'ib' : ''"
                    :style="{width: 100 - widthFakeSlider+'%'}"
                >
                    <!--Hier wird der Slider gerendert-->
                </div>
                <div
                    v-if="fakeslider"
                    class="fakeslider"
                    :style="{width: widthFakeSlider+'%'}"
                />
            </div>
            <div class="col col-2">
                <input
                    :id="'input-' + id "
                    :required="false"
                    :min="min"
                    :max="max"
                    :step="step"
                    :value="sliderWert/sliderToInputConversion"
                    type="number"
                    pattern="\d+"
                    class="display"
                    @change="changeInput"
                >
                <span
                    v-if="unit"
                    class="unit"
                >{{ unit }}</span>
                <div
                    v-if="inputAnnotation"
                    class="input-annotation"
                >
                    {{ inputAnnotation }}
                </div>
            </div>
            <info
                v-if="hinweisType === 'info'"
                :text="hinweisText"
            />
            <error
                v-else-if="hinweisType === 'error'"
                :text="hinweisText"
            />
        </div>
    </div>
</template>
<script>
import {defineComponent} from 'vue';
import noUiSlider from "nouislider";
import Tooltip from "@/components/Tooltip";
import Error from "@/components/rechner/Error";
import SliderTO from "@/types/SliderTO";
import {setValueInRangeAndStep} from "@/utils/NumberUtil";
import Info from "@/components/rechner/Info";

export default defineComponent({
    name: "Slider",
    components: {Info, Tooltip, Error},
    props: {
        sliderValues: {
            type: SliderTO,
            required: true
        },
        sliderToInputConversion: {
            type: Number,
            default: 1
        },
        widthFakeSlider: {
            type: Number,
            default: 0
        },
        fakeslider: {
            type: Boolean,
            default: false
        },
        id: {
            type: String,
            required: true
        },
        short: {
            type: Boolean,
            default: false
        },
        hinweisType: {
            type: String,
            default: 'error',
            validator: function (value) {
                // The value must match one of these strings
                return ['info', 'error'].indexOf(value) !== -1
            }
        },
        labelText: String,
        tooltipText: String,
        unit: String,
        inputAnnotation: String,
    },
    emits: ['valueChanged', 'slide', 'valueOutOfRange'],
    data() {
        return {
            rangeSlider: HTMLElement,
            sliderWert: this.sliderValues.value,
            showTooltip: false,
            isChangedByUser: false
        }
    },
    computed: {
        value() {
            return this.sliderValues.value
        },
        min() {
            return this.sliderValues.min
        },
        max() {
            return this.sliderValues.max
        },
        step() {
            return this.sliderValues.step
        },
        hinweisText() {
            if (this.sliderValues.hinweis && this.sliderValues.hinweis.text) {
                return this.sliderValues.hinweis.text;
            } else {
                return "";
            }
        }

    },
    watch: {
        value() {
            //value ist ein readonly-Property (aus dem Store) deshalb wird sliderWert verwendet und angepasst wenn der value sich von außen ändert
            this.sliderWert = this.value;
            this.rangeSlider.noUiSlider.set(this.sliderWert);
        },
        min() {
            this.adjustSlider();
        },
        max() {
            this.adjustSlider();
        },
        step() {
            this.adjustSlider();
        },
        widthFakeSlider() {
            this.adjustSlider();
        },
    },
    mounted() {
        this.rangeSlider = this.$refs['slider'];

        noUiSlider.create(this.rangeSlider, {
            start: this.value,
            step: this.step,
            connect: [true, false],
            range: {
                min: this.min,
                max: this.max
            },
        });

        /**
         * Zahl im Texteingabefeld beim Verschieben des Reglers aktualisieren.
         */
        this.rangeSlider.noUiSlider.on('slide', (values, handle) => {
            this.sliderWert = values[handle];
            this.$emit('slide', parseInt(this.sliderWert));
            this.isChangedByUser = true;
        });

        /**
         * Resultat berechnen, nachdem der Slider verschoben wurde (mouseup).
         */
        this.rangeSlider.noUiSlider.on('set', (values, handle) => {
            this.sliderWert = values[handle];
            if (this.isChangedByUser) //Wenn der Slider, durch die Response vom Backend geändert wurde, soll kein neuer Request gesendet werden
            {
                this.$emit('valueChanged', parseInt(this.sliderWert));
                this.isChangedByUser = false;
            }
        });
    },
    methods: {
        adjustSlider() { //Wenn sich min, max oder step nach der Initialisierung ändern

            if (this.rangeSlider.noUiSlider) {
                this.rangeSlider.noUiSlider.updateOptions({
                    value: this.sliderWert,
                    range: {
                        min: this.min,
                        max: this.max
                    },
                    step: this.step
                })
            }
        },
        changeInput(event) {
            let eventValue = event.target.value * this.sliderToInputConversion;

            this.sliderWert = setValueInRangeAndStep(eventValue, this.step, this.min, this.max);
            this.rangeSlider.noUiSlider.set(this.sliderWert);
            this.$emit('valueChanged', parseInt(this.sliderWert));

            if (eventValue < this.min || eventValue > this.max) {
                this.$emit('valueOutOfRange', eventValue, parseInt(this.sliderWert), this.min, this.max);
            }

        }
    }
});
</script>

<style scoped lang="less">
.slider-container {
    padding-bottom: 20px;
}

.col-slider {
    padding-top: 18px;
}

.col-fakeslider {
    padding-top: 6px;
}

.input-annotation {
    font-size: 12px;
    color: #425866;
}

.fakeslider {
    margin-top: -2px;
}

.klapper-content .noUi-target {
    margin-left: 2%;
}

.col.col-2 {
    float: none;

    @media @tablet-and-up {
        float: left;
    }
}

.unit {
    padding-left: @hSpacing*.25;
}
</style>
